import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import FileInput from "./FileInput";
import Button from "../../shared/button/Button";
import EmployerActionsList from "./EmployerActionsList";
import FormSubmitting from "./FormSubmitting";
import ApplicationRejectionReason from "../../../apiResources/ApplicationRejectionReason";
import {
  CandidateActionsWrapper,
  Form,
  Top,
  Bottom,
  ActionsButton,
  SelectedAction,
  RemoveAction,
  TextArea,
  AttachmentPopupTrigger,
  AttachmentPopup,
  AttachmentCounter,
  NotePopup,
  Note,
  CloseButton,
  PlaceholderBtn,
  TextAreaWrapper,
  RejectionReason,
  RejectionReasonSelect,
  RejectionReasonLabel,
  SubReasonsContainer,
  SubReasonsLabel,
  CheckBoxesGroup,
  CheckBoxesGroupItem,
  CheckBoxLabel,
  CheckBoxInput,
  Indicator,
  Required,
} from "./styles/candidate-actions-styles";

class EmployerCandidateActions extends Component {
  constructor(props) {
    super(props);

    this.state = {
      textareaFocused: false,
      rejectionReasons: [],
      selectedReason: "",
      selectedSubReasons: [],
      messageRequiredError: false,
    };
  }

  componentDidMount() {
    this.fetchRejectionReasons();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.message !== this.props.message && this.props.message.trim()) {
      this.setState({ messageRequiredError: false });
    }
  }

  fetchRejectionReasons = () => {
    ApplicationRejectionReason.all()
      .then((reasons) => {
        this.setState({ rejectionReasons: reasons.data });
      })
      .catch((err) => {
        this.setState({
          error: { message: "Failed to load rejection reasons" },
        });
      });
  };

  handleRejectionReasonChange = (e) => {
    this.setState({
      selectedReason: e.target.value,
      selectedSubReasons: [], // Clear sub-reasons when primary reason changes
      error: null,
    });
  };

  handleSubReasonChange = (e) => {
    const { value, checked } = e.target;
    const intValue = parseInt(value, 10);

    this.setState((prevState) => ({
      selectedSubReasons: checked
        ? [...prevState.selectedSubReasons, intValue]
        : prevState.selectedSubReasons.filter((r) => r !== intValue),
    }));
  };

  handleFormSubmit = (event) => {
    event.preventDefault();
    const { handleSubmit, action, message } = this.props;
    const { selectedReason, selectedSubReasons, rejectionReasons } = this.state;

     // When "Send Message" is selected, message is required
    if (action === "send_message") {
      if (!message || !message.trim()) {
        this.setState({ messageRequiredError: true });
        return;
      }
      this.setState({ messageRequiredError: false });
    }

    // If it's a rejected_candidate action, include rejection reason data
    if (action === "rejected_candidate") {
      const selectedReasonData = rejectionReasons.find(
        (r) => String(r.id) === String(selectedReason)
      );
      const isOtherReason = selectedReasonData?.name?.toLowerCase() === "other";
      if (isOtherReason && !message.trim()) {
        this.setState({
          error: { message: "Please provide feedback when selecting Other" },
        });
        return;
      }

      const rejectionData = {
        primaryReasonId: selectedReason,
        subReasonIds: selectedSubReasons,
      };

      // Call the parent handleSubmit with rejection data
      handleSubmit(event, rejectionData);
    } else {
      // For other actions, call handleSubmit normally
      handleSubmit(event);
    }
  };

  handleFocus = () => {
    const { textareaFocused } = this.state;
    this.setState({
      textareaFocused: !textareaFocused,
    });
  };

  resetRejectionReasonState = () => {
    this.setState({
      selectedReason: "",
      selectedSubReasons: [],
    });
  };

  handleActionsButtonClick = () => {
    const { toggleActionsDropdown } = this.props;

    // Reset rejection reasons when opening actions dropdown
    this.resetRejectionReasonState();

    // Call the original toggle function
    toggleActionsDropdown();
  };

  render() {
    const {
      actionsDropdown,
      attachmentChange,
      fileInput,
      fileSelected,
      handleMessageChange,
      message,
      offerMade,
      jobFilled,
      optionHandle,
      removeActionHandle,
      removeAttachment,
      resetButtonFocus,
      selectedAction,
      handleAttachmentPopup,
      attachmentPopupActive,
      isEmployer,
      action,
      jobStatus,
      formSubmitting,
    } = this.props;

    const {
      textareaFocused,
      rejectionReasons,
      selectedReason,
      selectedSubReasons,
      messageRequiredError,
    } = this.state;

    const selectedReasonData =
      action === "rejected_candidate" && selectedReason
        ? rejectionReasons.find((r) => String(r.id) === String(selectedReason))
        : null;
    const isOtherReason = selectedReasonData?.name?.toLowerCase() === "other";

    return (
      <CandidateActionsWrapper focused={textareaFocused}>
        {formSubmitting && <FormSubmitting />}
        <Form action="" onSubmit={this.handleFormSubmit}>
          <Top>
            {!selectedAction && (
              <ActionsButton
                onClick={this.handleActionsButtonClick}
                actionsDropdown={actionsDropdown}
                className="dropdown"
                data-test-id="actions-dropdown"
                selectedAction={selectedAction}
              >
                Add action
                <FontAwesomeIcon icon={faPlusCircle} />
              </ActionsButton>
            )}

            <SelectedAction active={selectedAction}>
              {selectedAction}
              <RemoveAction onClick={removeActionHandle} />
            </SelectedAction>
            <EmployerActionsList
              actionsDropdown={actionsDropdown}
              optionHandle={optionHandle}
              offerMade={offerMade}
              jobFilled={jobFilled}
              jobStatus={jobStatus}
            />
            {action === "rejected_candidate" && (
              <Fragment>
                <RejectionReason>
                  <RejectionReasonLabel>
                    Please choose a reason for rejection <Required>*</Required>
                  </RejectionReasonLabel>
                  <RejectionReasonSelect
                    value={selectedReason}
                    onChange={this.handleRejectionReasonChange}
                  >
                    <option value="">Select a Primary Reason</option>
                    {rejectionReasons.map((reason) => (
                      <option key={reason.id} value={String(reason.id)}>
                        {reason.name || reason.attributes?.name}
                      </option>
                    ))}
                  </RejectionReasonSelect>
                </RejectionReason>
              </Fragment>
            )}
            {action === "rejected_candidate" &&
              selectedReason &&
              !isOtherReason &&
              (() => {
                const hasSubReasons =
                  selectedReasonData?.subReasons?.length > 0;
                return hasSubReasons ? (
                  <SubReasonsContainer>
                    <SubReasonsLabel>
                      Select sub reasons (you can choose multiple)
                    </SubReasonsLabel>
                    <CheckBoxesGroup>
                      {selectedReasonData.subReasons.map((sub) => (
                        <CheckBoxesGroupItem key={sub.id}>
                          <CheckBoxLabel>
                            <CheckBoxInput
                              type="checkbox"
                              checked={selectedSubReasons.includes(
                                parseInt(sub.id, 10)
                              )}
                              value={sub.id}
                              onChange={this.handleSubReasonChange}
                              id={sub.id}
                            />
                            {sub.name}
                            <Indicator className="indicator" />
                          </CheckBoxLabel>
                        </CheckBoxesGroupItem>
                      ))}
                    </CheckBoxesGroup>
                  </SubReasonsContainer>
                ) : null;
              })()}
            {action === "rejected_candidate" && !selectedReason && (
              <Fragment>
                <NotePopup className="animated fadeInUp">
                  Before you reject, think of the human being who will receive
                  this.
                  <br />
                  If this were your application, what would you like as
                  constructive feedback?
                </NotePopup>
              </Fragment>
            )}
            {action === "rejected_candidate" && this.state.error && (
              <Note>{this.state.error.message}</Note>
            )}
          </Top>
          <AttachmentPopupTrigger
            selectedAction={selectedAction}
            textareaFocused={textareaFocused}
            onClick={selectedAction ? handleAttachmentPopup : null}
          />
          {fileSelected && <AttachmentCounter />}
          <AttachmentPopup active={attachmentPopupActive}>
            <CloseButton onClick={handleAttachmentPopup} />
            <FileInput
              ref={fileInput}
              fileSelected={fileSelected}
              attachmentChange={attachmentChange}
              removeAttachment={removeAttachment}
            />
          </AttachmentPopup>
          <Bottom marginTop={action === "rejected_candidate" && isOtherReason}>
            <TextAreaWrapper focused={textareaFocused}>
              <TextArea
                id="candidate-actions-ta"
                placeholder={
                  selectedAction && action === "rejected_candidate"
                    ? isOtherReason
                      ? "Please specify the reason for rejection"
                      : "Any other feedback..."
                    : selectedAction && "Enter message here..."
                }
                onChange={handleMessageChange}
                value={message}
                focused={textareaFocused}
                onFocus={this.handleFocus}
                onBlur={this.handleFocus}
                actionsActive={isEmployer}
                selectedAction={selectedAction}
                disabled={!selectedAction || formSubmitting}
              />
                {messageRequiredError && (
                <Note style={{ color: "#EB5757", marginTop: "4px" }}>
                  Message is required when sending a message.
                </Note>
              )}
            </TextAreaWrapper>
            {action === "rejected_candidate" &&
            (!selectedReason || (isOtherReason && !message.trim())) ? (
              <PlaceholderBtn data-test-id="fake-reject-button" />
            ) : (
              <Button
                classes="submit-action"
                disabled={
                  formSubmitting ||
                  !selectedAction ||
                  (action === "send_message" && !message.trim())
                }
                resetButtonFocus={resetButtonFocus}
                textareaFocused={textareaFocused}
                dataTestId="submit-action"
                id="submit-candidate-action"
              />
            )}
          </Bottom>
        </Form>
      </CandidateActionsWrapper>
    );
  }
}

EmployerCandidateActions.propTypes = {
  toggleActionsDropdown: PropTypes.func.isRequired,
  actionsDropdown: PropTypes.bool.isRequired,
  offerMade: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  selectedAction: PropTypes.string.isRequired,
  removeActionHandle: PropTypes.func.isRequired,
  optionHandle: PropTypes.func.isRequired,
  handleMessageChange: PropTypes.func.isRequired,
  fileInput: PropTypes.shape({ current: PropTypes.any }).isRequired,
  message: PropTypes.string.isRequired,
  fileSelected: PropTypes.bool.isRequired,
  attachmentChange: PropTypes.func.isRequired,
  isEmployer: PropTypes.bool,
  removeAttachment: PropTypes.func.isRequired,
  resetButtonFocus: PropTypes.bool.isRequired,
  handleAttachmentPopup: PropTypes.func.isRequired,
  attachmentPopupActive: PropTypes.bool.isRequired,
  jobFilled: PropTypes.bool,
  action: PropTypes.string,
  jobStatus: PropTypes.string.isRequired,
  formSubmitting: PropTypes.bool.isRequired,
};

EmployerCandidateActions.defaultProps = {
  jobFilled: PropTypes.isNull,
  isEmployer: PropTypes.isNull,
  action: PropTypes.isNull,
};

export default EmployerCandidateActions;
